<template>
  <div :class="containerClass">
    <page-header :title="$route.params.id ? 'Edit Sponsorship' : 'Post Sponsorship'" :container-class="null" />
    <div class="page-section">
      <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
        <b-form-group :label="`Schools`" label-for="school" label-class="form-label">
          <v-select
            id="school"
            class="form-control v-select-custom"
            label="name"
            :class="{
              'is-invalid': !$v.scholarship.schools.required && $v.scholarship.schools.$dirty
            }"
            v-model="scholarship.schools"
            :reduce="schools => schools.id"
            :placeholder="`Select Schools`"
            :options="allSchools"
            :loading="areSchoolsLoading"
            multiple
          >
            <template #search="{ attributes, events }">
              <input class="vs__search" v-bind="attributes" v-on="events" />
            </template>

            <template slot="option" slot-scope="option">
              <div class="d-flex align-items-center">
                <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside" size="xs">
                  <b-img :src="option.logo_url" class="img-fluid" width="20" alt="Logo" v-if="option.logo_url" />
                  <i class="fas fa-university fa-lg" v-else></i>
                </fmv-avatar>
                <span>{{ option.name }}</span>
              </div>
            </template>
            <template slot="selected-option" slot-scope="option">
              {{ option.name }}
            </template>
          </v-select>
          <b-form-invalid-feedback
            :state="!$v.scholarship.schools.required && $v.scholarship.schools.$dirty ? false : null"
            >This field is required.</b-form-invalid-feedback
          >
        </b-form-group>

        <b-form-group label="Title" label-for="title" label-class="form-label">
          <b-form-input
            id="title"
            placeholder="Enter sponsorship's title."
            v-model="scholarship.title"
            :state="!$v.scholarship.title.required && $v.scholarship.title.$dirty ? false : null"
          />
          <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Organization Name" label-for="title" label-class="form-label">
          <b-form-input id="organization" placeholder="Enter organization's name." v-model="scholarship.organization" />
        </b-form-group>

        <b-form-group label="External Sponsorship Type" label-for="ext-sponsorship-type" label-class="form-label">
          <b-form-select
            id="ext-sponsorship-type"
            v-model="scholarship.ext_sponsorship_type"
            :options="extSponsorshipTypeOptions"
            :state="
              !$v.scholarship.ext_sponsorship_type.required && $v.scholarship.ext_sponsorship_type.$dirty
                ? false
                : null
            "
          />
          <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group>
          <b-form-checkbox id="checkbox-featured" v-model="scholarship.is_featured" name="checkbox-featured" switch>
            Featured on CNAEnroll
          </b-form-checkbox>
          <small class="form-text text-muted">Shows in the Featured rows for its type and state. Leave off for ordinary listings.</small>
        </b-form-group>

        <b-form-group label="Description" label-for="description" label-class="form-label">
          <editor :init="editorInit" v-model="scholarship.description" placeholder="Enter sponsorship description" />
          <b-form-invalid-feedback :state="!$v.scholarship.description.required && $v.scholarship.description.$dirty ? false : null"
            >This field is required.</b-form-invalid-feedback
          >
        </b-form-group>

        <b-form-group label="Who Qualifies" label-for="who-qualifies" label-class="form-label">
          <editor :init="editorInit" v-model="scholarship.who_qualifies" placeholder="Enter who qualifies for this sponsorship" />
        </b-form-group>

        <b-form-group label="Requirements" label-for="requirements" label-class="form-label">
          <editor :init="editorInit" v-model="scholarship.requirements" placeholder="Enter sponsorship requirements" />
        </b-form-group>

        <b-form-group label="How To Apply" label-for="how-to-apply" label-class="form-label">
          <editor :init="editorInit" v-model="scholarship.how_to_apply" placeholder="Enter how to apply for this sponsorship" />
        </b-form-group>

        <b-form-group label="Call To Action" label-for="call-to-action" label-class="form-label">
          <b-form-input
            id="call-to-action"
            placeholder="Enter call to action"
            v-model="scholarship.call_to_action"
          />
        </b-form-group>

        <b-form-group label="Phone Number" label-for="phone-number" label-class="form-label">
          <b-form-input id="phone-number" placeholder="Enter contact phone number" v-model="scholarship.phone_number" />
        </b-form-group>

        <b-btn variant="primary" :disabled="isFormLoading" style="width: 150px" type="submit" class="btn-normal">
          <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
          <span v-else>{{ $route.params.id ? $t('update') : $t('add') }}</span>
        </b-btn>
      </b-form>
    </div>
  </div>
</template>

<script>
import Editor from '@tinymce/tinymce-vue';
import { required } from 'vuelidate/lib/validators';
import { mapGetters, mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import { FmvAvatar } from 'fmv-avatar';
import PageHeader from '@/components/Ui/PageHeader.vue';

export default {
  components: { PageHeader, FmvAvatar, Editor },
  extends: Page,

  data() {
    return {
      title: this.$route.params.id ? 'Edit Sponsorship' : 'Post Sponsorship',

      scholarship: {
        title: '',
        organization: '',
        schools: null,
        ext_sponsorship_type: 'Workforce vouchers',
        is_featured: false,
        description: '',
        call_to_action: '',
        who_qualifies: '',
        requirements: '',
        how_to_apply: '',
        phone_number: ''
      },
      editorInit: {
        height: 350,
        menubar: 'edit view insert format tools table help',
        plugins: [
          'advlist autolink lists link image charmap print preview anchor',
          'searchreplace visualblocks code fullscreen',
          'insertdatetime media table paste code help wordcount'
        ],
        toolbar:
          'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help'
      },
      extSponsorshipTypeOptions: [
        { value: 'Workforce vouchers', text: 'Workforce vouchers' },
        { value: 'Employer sponsorship', text: 'Employer sponsorship' },
        { value: 'State workforce grants', text: 'State workforce grants' },
        { value: 'Public benefits programs', text: 'Public benefits programs' },
        { value: 'Military and veterans', text: 'Military and veterans' },
        { value: 'Vocational rehabilitation', text: 'Vocational rehabilitation' },
        { value: 'Scholarships and nonprofits', text: 'Scholarships and nonprofits' },
        { value: 'Financing', text: 'Financing' }
      ],

      allSchools: [],
      isLoading: false,
      areSchoolsLoading: false
    };
  },

  validations() {
    return {
      scholarship: {
        title: { required },
        ext_sponsorship_type: { required },
        description: { required },

        schools: {
          required
        }
      }
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    isFormLoading() {
      return this.isLoading || this.areSchoolsLoading;
    }
  },

  watch: {},

  methods: {
    ...mapActions('scholarships', ['createScholarship', 'getScholarship', 'updateScholarship']),

    ...mapActions('school', ['getAllSchools']),

    async onSubmit() {
      this.isLoading = true;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        const data = {
          schools: !this.scholarship.schools.length ? [this.scholarship.schools] : this.scholarship.schools,
          title: this.scholarship.title,
          external_organization: this.scholarship.organization,
          ext_sponsorship_type: this.scholarship.ext_sponsorship_type,
          is_featured: !!this.scholarship.is_featured,
          description: this.scholarship.description,
          call_to_action: this.scholarship.call_to_action,
          who_qualifies: this.scholarship.who_qualifies,
          requirements: this.scholarship.requirements,
          how_to_apply: this.scholarship.how_to_apply,
          phone_number: this.scholarship.phone_number,
          scholarship_type: 'by_superadmin'
        };

        try {
          if (this.$route.params.id) {
            await this.updateScholarship({
              id: this.$route.params.id,
              data
            });
            this.makeToast({ variant: 'success', msg: 'Sponsorship Successfully Updated' });
          } else {
            await this.createScholarship(data);
            this.makeToast({ variant: 'success', msg: 'Sponsorship Successfully Posted' });
          }
          setTimeout(() => this.$router.push({ name: 'external-scholarships-list' }), 250);
        } catch (err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields correctly.' });
      }
      this.isLoading = false;
    },
    async fetchSchools() {
      this.areSchoolsLoading = true;
      try {
        const response = await this.getAllSchools({});

        this.allSchools = response.data;
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }

      this.areSchoolsLoading = false;
    },

    async fetchScholarship() {
      this.isLoading = true;
      try {
        const resp = await this.getScholarship(this.$route.params.id);

        this.scholarship.schools = resp.data.schools.map(emp => emp.id);
        this.scholarship.title = resp.data.title;
        this.scholarship.organization = resp.data.external_organization;
        this.scholarship.ext_sponsorship_type = resp.data.ext_sponsorship_type || 'Workforce vouchers';
        this.scholarship.is_featured = !!resp.data.is_featured;
        this.scholarship.description = resp.data.description || '';
        this.scholarship.call_to_action = resp.data.call_to_action || '';
        this.scholarship.who_qualifies = resp.data.who_qualifies || '';
        this.scholarship.requirements = resp.data.requirements || '';
        this.scholarship.how_to_apply = resp.data.how_to_apply || '';
        this.scholarship.phone_number = resp.data.phone_number || '';
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    }
  },

  async mounted() {
    await this.fetchSchools();
    if (this.$route.params.id) {
      await this.fetchScholarship();
    }
  }
};
</script>
